import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  ConfirmDialog,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { bankGatewayApi } from "@/services/api";
import {
  Building2,
  Plus,
  Settings,
  CheckCircle,
  Loader2,
  RefreshCw,
  Trash2,
  Landmark,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  X,
  CreditCard,
} from "lucide-react";
import { formatDate, formatCurrency } from "@/lib/utils";

interface BankConfig {
  id: string;
  bankCode: string;
  name: string;
  clientId?: string;
  certificateExpiresAt?: string;
  apiBaseUrl?: string;
  authBaseUrl?: string;
  isActive: boolean;
  lastTestedAt?: string;
  lastTestStatus?: string;
  accountCount?: number;
}

interface BankAccount {
  id: string;
  bankCode: string;
  bankName?: string;
  agency: string;
  accountNumber: string;
  accountDigit?: string;
  gatewayConfigId: string;
  accountType?: string;
  purpose?: string;
  cachedBalance?: number;
  cachedBalanceAt?: string;
  isActive: boolean;
}

interface BankTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: "credit" | "debit";
  counterpart?: {
    name?: string;
    document?: string;
    bank?: string;
    agency?: string;
    account?: string;
  };
  pix?: {
    endToEndId?: string;
    txId?: string;
  };
}

interface BankStatement {
  startDate: string;
  endDate: string;
  openingBalance: number;
  closingBalance: number;
  transactions: BankTransaction[];
}

const BANK_NAMES: Record<string, string> = {
  "341": "Itaú",
  "246": "ABC Brasil",
  "208": "BTG Pactual",
  "125": "Banco Genial",
};

export function BankGatewayPage() {
  const [configs, setConfigs] = useState<BankConfig[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);

  // Statement modal state
  const [statementModal, setStatementModal] = useState<{
    open: boolean;
    account: BankAccount | null;
    loading: boolean;
    statement: BankStatement | null;
    error: string | null;
  }>({
    open: false,
    account: null,
    loading: false,
    statement: null,
    error: null,
  });

  // Create config form state
  const [createForm, setCreateForm] = useState({
    bankCode: "341",
    name: "",
    clientId: "",
    clientSecret: "",
    apiBaseUrl: "",
    authBaseUrl: "",
  });
  const [creating, setCreating] = useState(false);

  // Create account form state
  const [createAccountForm, setCreateAccountForm] = useState({
    bankCode: "341",
    bankName: "Itaú",
    agency: "",
    accountNumber: "",
    accountDigit: "",
    gatewayConfigId: "",
    accountType: "CHECKING",
    purpose: "TREASURY",
  });
  const [creatingAccount, setCreatingAccount] = useState(false);

  // Confirm dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmText: string;
    variant: "default" | "danger";
    onConfirm: () => void;
    loading: boolean;
  }>({
    open: false,
    title: "",
    description: "",
    confirmText: "Confirmar",
    variant: "default",
    onConfirm: () => {},
    loading: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [configsData, accountsData] = await Promise.all([
        bankGatewayApi.listConfigs(),
        bankGatewayApi.listAccounts(),
      ]);
      setConfigs(configsData);
      setAccounts(accountsData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleTest(configId: string) {
    setTestingId(configId);
    try {
      await bankGatewayApi.testConfig(configId);
      await loadData();
    } catch (error) {
      console.error("Erro no teste:", error);
    } finally {
      setTestingId(null);
    }
  }

  async function handleCreate() {
    setCreating(true);
    try {
      await bankGatewayApi.createConfig({
        ...createForm,
        name: createForm.name || `${BANK_NAMES[createForm.bankCode]} - Config`,
      });
      setCreateForm({
        bankCode: "341",
        name: "",
        clientId: "",
        clientSecret: "",
        apiBaseUrl: "",
        authBaseUrl: "",
      });
      setShowCreateModal(false);
      await loadData();
    } catch (error) {
      console.error("Erro ao criar:", error);
    } finally {
      setCreating(false);
    }
  }

  async function handleCreateAccount() {
    setCreatingAccount(true);
    try {
      await bankGatewayApi.createAccount(createAccountForm);
      setCreateAccountForm({
        bankCode: "341",
        bankName: "Itaú",
        agency: "",
        accountNumber: "",
        accountDigit: "",
        gatewayConfigId: "",
        accountType: "CHECKING",
        purpose: "TREASURY",
      });
      setShowCreateAccountModal(false);
      await loadData();
    } catch (error) {
      console.error("Erro ao criar conta:", error);
    } finally {
      setCreatingAccount(false);
    }
  }

  function openConfirmDialog(
    title: string,
    description: string,
    confirmText: string,
    variant: "default" | "danger",
    onConfirm: () => Promise<void>
  ) {
    setConfirmDialog({
      open: true,
      title,
      description,
      confirmText,
      variant,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await onConfirm();
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
        } catch (error) {
          console.error("Erro na operação:", error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false,
    });
  }

  function closeConfirmDialog() {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }

  function handleDelete(configId: string) {
    openConfirmDialog(
      "Excluir Integração",
      "Tem certeza que deseja excluir esta configuração de integração bancária? Esta ação é irreversível.",
      "Excluir",
      "danger",
      async () => {
        await bankGatewayApi.deleteConfig(configId);
        await loadData();
      }
    );
  }

  async function handleRefreshBalance(accountId: string) {
    try {
      await bankGatewayApi.getAccountBalance(accountId);
      await loadData();
    } catch (error) {
      console.error("Erro ao atualizar saldo:", error);
    }
  }

  async function handleViewStatement(account: BankAccount) {
    setStatementModal({
      open: true,
      account,
      loading: true,
      statement: null,
      error: null,
    });

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      const statement = await bankGatewayApi.getAccountStatement(
        account.id,
        startDate.toISOString().split("T")[0],
        endDate.toISOString().split("T")[0]
      );

      setStatementModal((prev) => ({
        ...prev,
        loading: false,
        statement,
      }));
    } catch (error) {
      console.error("Erro ao carregar extrato:", error);
      setStatementModal((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : "Erro ao carregar extrato",
      }));
    }
  }

  function closeStatementModal() {
    setStatementModal({
      open: false,
      account: null,
      loading: false,
      statement: null,
      error: null,
    });
  }

  function getStatusBadge(config: BankConfig) {
    if (config.lastTestStatus === "success") {
      return <Badge variant="success">Conectado</Badge>;
    }

    if (config.certificateExpiresAt) {
      const expiresAt = new Date(config.certificateExpiresAt);
      const daysUntilExpiry = Math.ceil(
        (expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      );

      if (daysUntilExpiry < 0) {
        return <Badge variant="destructive">Cert Expirado</Badge>;
      }
      if (daysUntilExpiry < 30) {
        return <Badge variant="warning">Cert expira em {daysUntilExpiry}d</Badge>;
      }
    }

    return <Badge variant="secondary">Não testado</Badge>;
  }

  const connectedConfigs = configs.filter((c) => c.lastTestStatus === "success").length;

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Landmark className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Barramento Bancário"
          description="Integrações com APIs bancárias"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Landmark className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Barramento Bancário"
        description="Integrações com APIs bancárias"
        stats={[
          { value: configs.length, label: "Integrações" },
          { value: connectedConfigs, label: "Conectadas" },
          { value: accounts.length, label: "Contas" },
        ]}
        actions={
          <div className="flex gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => setShowCreateAccountModal(true)}
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Nova Conta
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Integração
            </Button>
          </div>
        }
      />

      {/* Configs Section */}
      <h2 className="text-lg font-semibold">Integrações Configuradas</h2>

      {configs.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhuma integração bancária configurada</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setShowCreateModal(true)}
            >
              Criar primeira integração
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {configs.map((config) => (
            <Card key={config.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center font-bold">
                      {config.bankCode}
                    </div>
                    <div>
                      <p className="font-medium">{config.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {BANK_NAMES[config.bankCode] || config.bankCode}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(config)}
                </div>

                <div className="mt-4 text-sm text-muted-foreground">
                  {config.accountCount !== undefined && (
                    <p>{config.accountCount} conta(s) vinculada(s)</p>
                  )}
                  {config.lastTestedAt && (
                    <p>Testado: {formatDate(config.lastTestedAt)}</p>
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleTest(config.id)}
                    disabled={testingId === config.id}
                  >
                    {testingId === config.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : config.lastTestStatus === "success" ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    <span className="ml-2">Testar</span>
                  </Button>
                  <Button size="sm" variant="outline">
                    <Settings className="w-4 h-4" />
                    <span className="ml-2">Editar</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(config.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Accounts Section */}
      <h2 className="text-lg font-semibold mt-8">Contas Bancárias</h2>

      {accounts.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <p>Nenhuma conta bancária vinculada</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setShowCreateAccountModal(true)}
            >
              Cadastrar primeira conta
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="border-b border-border">
                <tr className="text-left text-sm text-muted-foreground">
                  <th className="p-4 font-medium">Banco</th>
                  <th className="p-4 font-medium">Agência/Conta</th>
                  <th className="p-4 font-medium">Propósito</th>
                  <th className="p-4 font-medium">Saldo</th>
                  <th className="p-4 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr
                    key={account.id}
                    className="border-b border-border last:border-0"
                  >
                    <td className="p-4">
                      <p className="font-medium">
                        {account.bankName || BANK_NAMES[account.bankCode]}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {account.bankCode}
                      </p>
                    </td>
                    <td className="p-4 font-mono">
                      {account.agency} / {account.accountNumber}
                      {account.accountDigit && `-${account.accountDigit}`}
                    </td>
                    <td className="p-4">
                      <Badge variant="secondary">
                        {account.purpose || "N/A"}
                      </Badge>
                    </td>
                    <td className="p-4">
                      {account.cachedBalance !== undefined ? (
                        <div>
                          <p className="font-medium">
                            {formatCurrency(account.cachedBalance / 100)}
                          </p>
                          {account.cachedBalanceAt && (
                            <p className="text-xs text-muted-foreground">
                              {formatDate(account.cachedBalanceAt)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRefreshBalance(account.id)}
                          title="Atualizar saldo"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewStatement(account)}
                          title="Ver extrato"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Create Config Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Nova Integração Bancária</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Banco</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={createForm.bankCode}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, bankCode: e.target.value })
                  }
                >
                  <option value="341">341 - Itaú</option>
                  <option value="246">246 - ABC Brasil</option>
                  <option value="208">208 - BTG Pactual</option>
                  <option value="125">125 - Banco Genial</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">Nome da Integração</label>
                <Input
                  placeholder="Ex: Itaú - Arrecadação"
                  value={createForm.name}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, name: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="text-sm font-medium">Client ID</label>
                <Input
                  value={createForm.clientId}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, clientId: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="text-sm font-medium">Client Secret</label>
                <Input
                  type="password"
                  value={createForm.clientSecret}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      clientSecret: e.target.value,
                    })
                  }
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={creating}>
                  {creating && (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  )}
                  Criar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Account Modal */}
      {showCreateAccountModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Nova Conta Bancária</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Integração</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={createAccountForm.gatewayConfigId}
                  onChange={(e) => {
                    const config = configs.find((c) => c.id === e.target.value);
                    setCreateAccountForm({
                      ...createAccountForm,
                      gatewayConfigId: e.target.value,
                      bankCode: config?.bankCode || "341",
                      bankName: config?.name || "Itaú",
                    });
                  }}
                >
                  <option value="">Selecione uma integração</option>
                  {configs.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.name} ({BANK_NAMES[config.bankCode]})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1">
                  <label className="text-sm font-medium">Agência</label>
                  <Input
                    placeholder="0000"
                    value={createAccountForm.agency}
                    onChange={(e) =>
                      setCreateAccountForm({ ...createAccountForm, agency: e.target.value })
                    }
                  />
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-medium">Conta</label>
                  <Input
                    placeholder="00000"
                    value={createAccountForm.accountNumber}
                    onChange={(e) =>
                      setCreateAccountForm({ ...createAccountForm, accountNumber: e.target.value })
                    }
                  />
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-medium">Dígito</label>
                  <Input
                    placeholder="0"
                    value={createAccountForm.accountDigit}
                    onChange={(e) =>
                      setCreateAccountForm({ ...createAccountForm, accountDigit: e.target.value })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Tipo de Conta</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={createAccountForm.accountType}
                  onChange={(e) =>
                    setCreateAccountForm({ ...createAccountForm, accountType: e.target.value })
                  }
                >
                  <option value="CHECKING">Conta Corrente</option>
                  <option value="SAVINGS">Poupança</option>
                  <option value="PAYMENT">Conta Pagamento</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">Propósito</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={createAccountForm.purpose}
                  onChange={(e) =>
                    setCreateAccountForm({ ...createAccountForm, purpose: e.target.value })
                  }
                >
                  <option value="TREASURY">Tesouraria</option>
                  <option value="COLLECTION">Arrecadação</option>
                  <option value="PAYMENT">Pagamentos</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateAccountModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreateAccount}
                  disabled={creatingAccount || !createAccountForm.gatewayConfigId}
                >
                  {creatingAccount && (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  )}
                  Criar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Statement Modal */}
      {statementModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between border-b">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Extrato Bancário
                </CardTitle>
                {statementModal.account && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {statementModal.account.bankName || BANK_NAMES[statementModal.account.bankCode]} -{" "}
                    Ag {statementModal.account.agency} / CC {statementModal.account.accountNumber}
                    {statementModal.account.accountDigit && `-${statementModal.account.accountDigit}`}
                  </p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={closeStatementModal}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-0">
              {statementModal.loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : statementModal.error ? (
                <div className="text-center py-12 text-destructive">
                  <p>{statementModal.error}</p>
                </div>
              ) : statementModal.statement ? (
                <div>
                  {/* Summary */}
                  <div className="p-4 bg-muted/50 border-b flex gap-6">
                    <div>
                      <p className="text-xs text-muted-foreground">Período</p>
                      <p className="font-medium">
                        {formatDate(statementModal.statement.startDate)} a{" "}
                        {formatDate(statementModal.statement.endDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Saldo Final</p>
                      <p className="font-medium text-lg">
                        {formatCurrency(statementModal.statement.closingBalance / 100)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Transações</p>
                      <p className="font-medium">{statementModal.statement.transactions.length}</p>
                    </div>
                  </div>

                  {/* Transactions */}
                  {statementModal.statement.transactions.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
                      <p>Nenhuma transação no período</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {statementModal.statement.transactions.map((tx) => (
                        <div
                          key={tx.id}
                          className="p-4 flex items-center justify-between hover:bg-muted/30"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                tx.type === "credit"
                                  ? "bg-success/10 text-success"
                                  : "bg-destructive/10 text-destructive"
                              }`}
                            >
                              {tx.type === "credit" ? (
                                <ArrowDownRight className="w-4 h-4" />
                              ) : (
                                <ArrowUpRight className="w-4 h-4" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm">
                                {tx.description || "Operação bancária"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatDate(tx.date)}
                                {tx.counterpart?.name && ` • ${tx.counterpart.name}`}
                              </p>
                              {tx.pix?.endToEndId && (
                                <p className="text-xs text-muted-foreground font-mono">
                                  E2E: {tx.pix.endToEndId}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p
                              className={`font-semibold ${
                                tx.type === "credit" ? "text-success" : "text-destructive"
                              }`}
                            >
                              {tx.type === "credit" ? "+" : "-"}{" "}
                              {formatCurrency(tx.amount / 100)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
      />
    </div>
  );
}
